import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, ShieldAlert, Loader2, QrCode, User, Mail, KeyRound, Check } from "lucide-react";
import { toast } from "sonner";

const AVATAR_COLORS = ["#2563EB", "#7C3AED", "#DB2777", "#DC2626", "#EA580C",
                         "#CA8A04", "#16A34A", "#0D9488", "#0891B2", "#4F46E5"];

function initials(name) {
  return (name || "?").trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase()).join("") || "?";
}

function ProfileSection() {
  const { user, refreshUser } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [color, setColor] = useState(user?.avatar_color || AVATAR_COLORS[0]);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/auth/profile", { name, avatar_color: color });
      await refreshUser();
      toast.success("Profile updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-border bg-card">
      <div className="flex items-center gap-3 border-b border-border px-6 py-5">
        <User className="h-5 w-5 text-muted-foreground" />
        <div>
          <p className="font-heading font-bold tracking-tight">Profile</p>
          <p className="text-sm text-muted-foreground">Your display name and avatar.</p>
        </div>
      </div>
      <div className="space-y-5 p-6">
        <div className="flex items-center gap-4">
          <div className="flex h-14 w-14 items-center justify-center rounded-full text-lg font-bold text-white" style={{ backgroundColor: color }}>
            {initials(name)}
          </div>
          <div className="flex flex-wrap gap-2">
            {AVATAR_COLORS.map((c) => (
              <button key={c} type="button" onClick={() => setColor(c)}
                      className="flex h-7 w-7 items-center justify-center rounded-full ring-offset-2"
                      style={{ backgroundColor: c, boxShadow: color === c ? `0 0 0 2px ${c}` : "none" }}
                      data-testid={`avatar-color-${c}`}>
                {color === c && <Check className="h-4 w-4 text-white" />}
              </button>
            ))}
          </div>
        </div>
        <div className="max-w-xs space-y-2">
          <Label className="text-xs uppercase tracking-[0.15em]">Display name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name-input" />
        </div>
        <Button onClick={save} disabled={busy || !name.trim()} className="gap-2" data-testid="profile-save-button">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          Save profile
        </Button>
      </div>
    </div>
  );
}

function EmailSection() {
  const { user, refreshUser } = useAuth();
  const [newEmail, setNewEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/auth/change-email", { new_email: newEmail, password });
      await refreshUser();
      setNewEmail(""); setPassword("");
      toast.success("Email updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-border bg-card">
      <div className="flex items-center gap-3 border-b border-border px-6 py-5">
        <Mail className="h-5 w-5 text-muted-foreground" />
        <div>
          <p className="font-heading font-bold tracking-tight">Email address</p>
          <p className="text-sm text-muted-foreground">Currently <span className="font-mono">{user?.email}</span></p>
        </div>
      </div>
      <div className="space-y-3 p-6">
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-[0.15em]">New email</Label>
          <Input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} data-testid="new-email-input" />
        </div>
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-[0.15em]">Confirm password</Label>
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="email-password-input" />
        </div>
        <Button onClick={save} disabled={busy || !newEmail || !password} className="gap-2" data-testid="change-email-button">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          Update email
        </Button>
      </div>
    </div>
  );
}

function PasswordSection() {
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: currentPw, new_password: newPw });
      setCurrentPw(""); setNewPw("");
      toast.success("Password updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-border bg-card">
      <div className="flex items-center gap-3 border-b border-border px-6 py-5">
        <KeyRound className="h-5 w-5 text-muted-foreground" />
        <div>
          <p className="font-heading font-bold tracking-tight">Password</p>
          <p className="text-sm text-muted-foreground">Change your account password.</p>
        </div>
      </div>
      <div className="space-y-3 p-6">
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-[0.15em]">Current password</Label>
          <Input type="password" value={currentPw} onChange={(e) => setCurrentPw(e.target.value)} data-testid="current-password-input" />
        </div>
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-[0.15em]">New password</Label>
          <Input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} data-testid="new-password-input" />
        </div>
        <Button onClick={save} disabled={busy || !currentPw || newPw.length < 8} className="gap-2" data-testid="change-password-button">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          Update password
        </Button>
      </div>
    </div>
  );
}

export default function Settings() {
  const { user, refreshUser } = useAuth();
  const [setup, setSetup] = useState(null); // { secret, qr, otpauth_uri }
  const [code, setCode] = useState("");
  const [pwd, setPwd] = useState("");
  const [busy, setBusy] = useState(false);

  const startSetup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/setup");
      setSetup(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const enable = async () => {
    setBusy(true);
    try {
      await api.post("/auth/2fa/enable", { code });
      await refreshUser();
      setSetup(null);
      setCode("");
      toast.success("2FA enabled");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", { password: pwd });
      await refreshUser();
      setPwd("");
      toast.success("2FA disabled");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const enabled = user?.totp_enabled;

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div className="mb-2">
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Account</p>
        <h1 className="mt-1 font-heading text-4xl font-bold tracking-tighter">Account Settings</h1>
      </div>

      <ProfileSection />

      <div className="grid gap-6 md:grid-cols-2">
        <EmailSection />
        <PasswordSection />
      </div>

      <div className="border border-border bg-card">
        <div className="flex items-center gap-3 border-b border-border px-6 py-5">
          {enabled ? <ShieldCheck className="h-5 w-5 text-emerald-600" /> : <ShieldAlert className="h-5 w-5 text-amber-600" />}
          <div>
            <p className="font-heading font-bold tracking-tight">Two-Factor Authentication</p>
            <p className="text-sm text-muted-foreground">
              {enabled ? "Enabled — your account requires an authenticator code." : "Add a second layer with Google Authenticator / Authy."}
            </p>
          </div>
          <span className="ml-auto text-xs font-medium" data-testid="twofa-status">
            {enabled ? <span className="text-emerald-600">ON</span> : <span className="text-muted-foreground">OFF</span>}
          </span>
        </div>

        <div className="p-6">
          {!enabled && !setup && (
            <Button onClick={startSetup} disabled={busy} className="gap-2" data-testid="twofa-setup-button">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <QrCode className="h-4 w-4" />}
              Set up 2FA
            </Button>
          )}

          {!enabled && setup && (
            <div className="space-y-5">
              <p className="text-sm">1. Scan this QR code in your authenticator app:</p>
              <img src={setup.qr} alt="2FA QR code" className="h-44 w-44 rounded-md border border-border" data-testid="twofa-qr" />
              <p className="text-xs text-muted-foreground">
                Or enter this key manually: <span className="font-mono text-foreground">{setup.secret}</span>
              </p>
              <div className="max-w-xs space-y-2">
                <Label className="text-xs uppercase tracking-[0.15em]">2. Enter the 6-digit code</Label>
                <Input value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" maxLength={6}
                       placeholder="123456" className="font-mono tracking-[0.4em]" data-testid="twofa-enable-code-input" />
              </div>
              <Button onClick={enable} disabled={busy || code.length < 6} className="gap-2" data-testid="twofa-enable-button">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
                Verify & enable
              </Button>
            </div>
          )}

          {enabled && (
            <div className="max-w-xs space-y-3">
              <Label className="text-xs uppercase tracking-[0.15em]">Confirm password to disable</Label>
              <Input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)}
                     placeholder="Your password" data-testid="twofa-disable-password-input" />
              <Button variant="outline" onClick={disable} disabled={busy || !pwd} className="gap-2" data-testid="twofa-disable-button">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldAlert className="h-4 w-4" />}
                Disable 2FA
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
